import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";

const LIKERT = [
  { v: 1, l: "Strongly disagree" }, { v: 2, l: "Disagree" }, { v: 3, l: "Neutral" },
  { v: 4, l: "Agree" }, { v: 5, l: "Strongly agree" },
];
const PER_PAGE = 4;

export default function Assessment() {
  const { profile, loadProfile } = useAuth();
  const pid = profile?.id;
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pid) return;
    (async () => {
      const [q, d] = await Promise.all([api.get("/questions"), api.get(`/profiles/${pid}/assessment/draft`)]);
      setQuestions(q.data);
      setAnswers(d.data.answers || {});
    })();
  }, [pid]);

  if (profile === null) return <Navigate to="/setup" replace />;

  const answer = (qid, v) => {
    const next = { ...answers, [qid]: v };
    setAnswers(next);
    api.put(`/profiles/${pid}/assessment/draft`, { answers: next }).catch(() => {});
  };

  const totalPages = Math.ceil(questions.length / PER_PAGE);
  const pageQs = questions.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const answered = Object.keys(answers).length;
  const pageComplete = pageQs.every((q) => answers[q.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/profiles/${pid}/assessment`, { answers });
      await loadProfile();
      toast.success("Your psychology is woven in.");
      nav("/dashboard");
    } catch { toast.error("Could not submit"); } finally { setSubmitting(false); }
  };

  if (!questions.length) return <Shell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div></Shell>;

  return (
    <Shell>
      <div className="max-w-2xl mx-auto">
        <p className="overline mb-2">Psychology · Big Five · MBTI · Enneagram</p>
        <h1 className="font-serif text-4xl mb-3">A few honest questions.</h1>
        <div className="h-1 bg-muted rounded-full overflow-hidden mb-10">
          <div className="h-full bg-primary transition-all" style={{ width: `${(answered / questions.length) * 100}%` }} data-testid="assessment-progress" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={page} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-8">
            {pageQs.map((q) => (
              <div key={q.id} data-testid={`question-${q.id}`}>
                <p className="text-lg mb-4">{q.text}</p>
                <div className="flex flex-wrap gap-2">
                  {LIKERT.map((o) => (
                    <button key={o.v} data-testid={`q${q.id}-opt-${o.v}`} onClick={() => answer(q.id, o.v)}
                      className={`text-sm px-4 py-2 rounded-full border transition-colors ${answers[q.id] === o.v ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/40"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-12">
          <button data-testid="assess-back" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
            className={`flex items-center gap-1 text-sm ${page === 0 ? "opacity-0" : "text-muted-foreground hover:text-foreground"}`}>
            <ArrowLeft size={16} /> Back
          </button>
          {page < totalPages - 1 ? (
            <button data-testid="assess-next" disabled={!pageComplete} onClick={() => setPage((p) => p + 1)}
              className="gold-btn px-7 py-3 rounded-full font-medium flex items-center gap-2 disabled:opacity-40">
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="assess-submit" disabled={answered < questions.length || submitting} onClick={submit}
              className="gold-btn px-7 py-3 rounded-full font-medium flex items-center gap-2 disabled:opacity-40">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : "Complete"}
            </button>
          )}
        </div>
      </div>
    </Shell>
  );
}
